import React, { useCallback, useEffect, useState } from 'react';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import styled from 'styled-components';
import { IWholesaleMarket, wholesaleMarketConstants } from '../../../constants/cropTrends/wholesaleMarket.constant';
import { chunkArrayForQuadSwiper } from '../../../utils/chunkArrayForQuadSwiper.util';
import { CheckboxCircle } from '../../../components/common/inputs/CheckboxCircle';
import { useGetCropFavorite } from '../../../hooks/api/crop-trends/useGetCropFavorite.query';
import { usePostCropFavorite } from '../../../hooks/api/crop-trends/mutations/usePostCropFavorite.mutation';
import { usePatchCropFavorite } from '../../../hooks/api/crop-trends/mutations/usePatchCropFavorite.mutation';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { browserStorage } from '../../../utils/browserStorage.util';
import { findCropFavorite } from '../../../utils/finder/findCropFavorite.util';
import { ampli } from '../../../ampli';

interface ChooseWholesaleMarketModalProps {
  isShow: boolean;
  closeModal: () => void;
  largeCategoryCode: string;
  middleCategoryCode: string;
  smallCategoryCode: string;
  isFavoriteModalOn: string;
  cropCode: string;
  cropName: string;
  selectedMarketsBeforeLogin: string;
}

const chunk = <T,>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const ChooseWholesaleMarketModal = ({
  isShow,
  closeModal,
  largeCategoryCode,
  middleCategoryCode,
  smallCategoryCode,
  isFavoriteModalOn,
  cropCode,
  cropName,
  selectedMarketsBeforeLogin,
}: ChooseWholesaleMarketModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { data: myCropFavorite } = useGetCropFavorite();

  const { mutate: postCropFavorite } = usePostCropFavorite();
  const { mutate: patchCropFavorite } = usePatchCropFavorite();

  const [isMyCropFavoriteChecked, setIsMyCropFavoriteChecked] = useState<boolean>(false);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [isConfirmModalShow, setIsConfirmModalShow] = useState<boolean>(false);
  const [isLoginNeededModalShow, setIsLoginNeededModalShow] = useState<boolean>(false);

  const [savedSelectedMarkets, setSavedSelectedMarkets] = useState<string>();

  const wholesaleMarketChunks = chunk(wholesaleMarketConstants, 2);

  const resetSelectedMarket = () => {
    setSelectedMarkets([]);
  };

  const resetBeforeLoginSelectInfo = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('isFavoriteModalOn', 'false');
    newParams.set('cropCode', '');
    newParams.set('cropName', '');
    browserStorage.setItem({ storageType: 'sessionStorage', itemKey: 'storedPageForRedirection', value: '' });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleModalClose = () => {
    resetSelectedMarket();
    closeModal();
  };

  const handleMarketCardClick = (item: IWholesaleMarket) => {
    setSelectedMarkets((prev) => {
      if (prev.includes(item.code)) {
        return prev.filter((code) => code !== item.code);
      }

      if (prev.length >= 5) {
        return prev;
      }

      return [...prev, item.code];
    });
  };

  const handleChooseCompleteButtonClick = () => {
    if (!isUserLoggedIn) {
      setSavedSelectedMarkets(selectedMarkets.join(','));
      handleModalClose();
      setIsLoginNeededModalShow(true);
      return;
    }

    if (selectedMarkets.length === 0) {
      return;
    }

    const reqBody = {
      reqBody: {
        largeCategoryCode,
        middleCategoryCode,
        smallCategoryCode,
        wholesaleMarketCodes: selectedMarkets,
      },
    };

    const onSuccess = () => {
      handleModalClose();
      setIsConfirmModalShow(true);
      ampli.pvmCompleteAddFavoriteCrop({
        cultivar: cropName,
        wholesale_markets: selectedMarkets,
      });
    };

    if (myCropFavorite && myCropFavorite.length > 0) {
      const { isFavoriteCrop } = findCropFavorite({
        largeCategoryCode,
        middleCategoryCode,
        smallCategoryCode,
        cropFavoritesInfos: myCropFavorite,
      });

      if (isFavoriteCrop) {
        patchCropFavorite(reqBody, { onSuccess });
        return;
      }
    }

    postCropFavorite(reqBody, { onSuccess });
  };

  useEffect(() => {
    if (isShow) {
      setIsMyCropFavoriteChecked(false);
    }
  }, [isShow]);

  useEffect(() => {
    if (isMyCropFavoriteChecked === true) {
      return;
    }

    if (myCropFavorite && myCropFavorite.length > 0) {
      const { isFavoriteCrop, wholesaleMarketCodes } = findCropFavorite({
        largeCategoryCode,
        middleCategoryCode,
        smallCategoryCode,
        cropFavoritesInfos: myCropFavorite,
      });

      if (isFavoriteCrop) {
        setSelectedMarkets((prev) => [...wholesaleMarketCodes]);
        resetBeforeLoginSelectInfo();
      }
    }
    setIsMyCropFavoriteChecked(true);
  }, [
    isMyCropFavoriteChecked,
    myCropFavorite,
    largeCategoryCode,
    middleCategoryCode,
    smallCategoryCode,
    resetBeforeLoginSelectInfo,
    setSelectedMarkets,
    setIsMyCropFavoriteChecked,
  ]);

  useEffect(() => {
    if (isMyCropFavoriteChecked === false || (selectedMarkets && selectedMarkets.length > 0)) {
      return;
    }

    if (isFavoriteModalOn === 'true' && !!cropCode && !!cropName && !!selectedMarketsBeforeLogin) {
      const previousSelectedMarketArray = selectedMarketsBeforeLogin.split(',');
      setSelectedMarkets((prev) => [...previousSelectedMarketArray]);
      resetBeforeLoginSelectInfo();
    }
  }, [
    isMyCropFavoriteChecked,
    selectedMarkets,
    isFavoriteModalOn,
    cropCode,
    cropName,
    selectedMarketsBeforeLogin,
    searchParams,
    setSearchParams,
    resetBeforeLoginSelectInfo,
  ]);

  return (
    <>
      <PopupConfirmModal
        isShow={isShow}
        hasCloseButton={false}
        closeModal={handleModalClose}
        title={'도매 시장을 선택해주세요'}
        description={`최대 ${5 - selectedMarkets.length}개까지 선택할 수 있어요.`}
        element={
          <Container>
            <CheckboxCardContainer>
              {wholesaleMarketChunks.map((chunk, chunkIndex) => (
                <Row key={`row-${chunk[0]?.code || chunkIndex}`}>
                  {chunk.map((item) => (
                    <CheckboxCard key={item.code} onClick={() => handleMarketCardClick(item)}>
                      <CheckboxCardTitleWrapper>
                        <CheckboxCardTitle>{item.name}</CheckboxCardTitle>
                      </CheckboxCardTitleWrapper>
                      <CheckboxCircle isSelected={selectedMarkets.includes(item.code)} />
                    </CheckboxCard>
                  ))}
                </Row>
              ))}
            </CheckboxCardContainer>
            <BlurSection />
          </Container>
        }
        buttonArray={[
          {
            text: '선택 완료',
            onClick: handleChooseCompleteButtonClick,
            disabled: selectedMarkets.length === 0,
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: handleModalClose,
            color: 'gray',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isConfirmModalShow}
        closeModal={() => setIsConfirmModalShow(false)}
        title={'⭐ 관심 농산물 등록 완료!'}
        description={'농산물 시세 목록 페이지에서\n빠르게 확인 가능해요'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsConfirmModalShow(false),
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isLoginNeededModalShow}
        hasCloseButton={false}
        closeModal={() => setIsLoginNeededModalShow(false)}
        title={'로그인이 필요합니다.'}
        description={'로그인하러 가시겠습니까?'}
        buttonArray={[
          {
            text: '로그인',
            onClick: () => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('isFavoriteModalOn', 'true');
              newParams.set('cropCode', `${largeCategoryCode}-${middleCategoryCode}-${smallCategoryCode}`);
              newParams.set('cropName', cropName);
              newParams.set('selectedMarketsBeforeLogin', savedSelectedMarkets ?? '');
              browserStorage.setItem({
                storageType: 'sessionStorage',
                itemKey: 'storedPageForRedirection',
                value: `${location.pathname}?${newParams}`,
              });
              navigate(pageInfo.loginPage.path);
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setIsLoginNeededModalShow(false),
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;

  position: relative;
`;

const CheckboxCardContainer = styled.div`
  height: 228px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-radius: var(--radius-none, 0px);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  overflow: scroll;

  padding-bottom: 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const CheckboxCard = styled.div`
  height: 52px;

  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  padding: 8px 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const CheckboxCardTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
`;

const CheckboxCardTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  flex: 1 0 0;
`;

const BlurSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  padding: 16px;

  position: absolute;
  bottom: -24px;

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  pointer-events: none;
`;
